import type { Batch, Order, Shop, Subscription } from '@notify/shared';
import type { OrderStatus, RushMode } from '@notify/shared';

import { api } from './api';

// ─── Response shapes ──────────────────────────────────────────────────────────

export interface OwnerProfile {
  id: string;
  name: string;
  email: string;
  createdAt: Date;
}

export interface AvailablePlan {
  id: string;
  name: string;
  title: string;
  description: string;
  price_monthly: number;
  duration_days: number;
  daily_notification_limit: number;
  max_shops: number;
  max_qr_codes: number;
  features: string[];
  is_active: boolean;
  is_popular: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export type ShopSubscription = Subscription | { plan: string; status: string };

export interface ShopStats {
  total_orders: number;
  pending: number;
  preparing: number;
  ready: number;
  collected: number;
  notifications_sent: number;
  avg_prep_time_minutes: number;
}

export interface OrderWithSubscribers extends Order {
  subscriber_count: number;
}

export interface NotifyBatchResult {
  orders_notified: number;
  batch: Batch;
}

export interface UsageStats {
  used: number;
  limit: number | null;
  plan: string;
  percentUsed: number;
}

export interface QRCodeResult {
  hasQR: boolean;
  token?: string;
  qr_image_url?: string;
  linked_at?: string;
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const shopService = {
  getMyShop: () =>
    api.get<Shop>('/api/v1/shops'),

  getShopStats: () =>
    api.get<ShopStats>('/api/v1/shops/stats'),

  updateRushMode: (rush_mode: RushMode, prep_time_override?: number) =>
    api.patch<Shop>('/api/v1/shops/rush-mode', { rush_mode, prep_time_override }),

  getOrders: (params?: { status?: string; date?: string }) =>
    api.get<Order[]>('/api/v1/orders', params ? { params } : undefined),

  createOrder: (data: {
    order_reference: string;
    batch_id?: string;
    prep_time_override?: number;
  }) => api.post<Order>('/api/v1/orders', data),

  updateOrderStatus: (orderId: string, status: OrderStatus) =>
    api.patch<Order>(`/api/v1/orders/${orderId}/status`, { status }),

  getOrder: (orderId: string) =>
    api.get<OrderWithSubscribers>(`/api/v1/orders/${orderId}`),

  getBatches: () =>
    api.get<Batch[]>('/api/v1/orders/batches'),

  createBatch: (label: string) =>
    api.post<Batch>('/api/v1/orders/batches', { label }),

  notifyBatch: (batchId: string) =>
    api.post<NotifyBatchResult>(`/api/v1/orders/batches/${batchId}/notify`),

  getUsage: () =>
    api.get<UsageStats>('/api/v1/subscriptions/usage'),

  getMySubscription: () =>
    api.get<ShopSubscription>('/api/v1/subscriptions'),

  getOwnerProfile: () =>
    api.get<OwnerProfile>('/api/v1/shops/profile'),

  updateOwnerProfile: (data: { name?: string; email?: string }) =>
    api.patch<OwnerProfile>('/api/v1/shops/profile', data),

  updateShopDetails: (data: { name?: string; location?: string }) =>
    api.patch<Shop>('/api/v1/shops/details', data),

  getAvailablePlans: () =>
    api.get<AvailablePlan[]>('/api/v1/subscriptions/plans'),

  getMyQRCode: () =>
    api.get<QRCodeResult>('/api/v1/shops/my-qr'),
};
